import { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import type { ImageDto } from './types';

export interface ImagePreviewModalProps {
  images: ImageDto[];
  initialIndex: number;
  onClose: () => void;
}

/**
 * Full-size image preview modal with navigation
 * - View full-size images
 * - Navigate with arrow buttons or keyboard
 * - Download current image
 * - Close with X button or Escape key
 */
export function ImagePreviewModal({ images, initialIndex, onClose }: ImagePreviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const currentImage = images[currentIndex];
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, images.length]);

  const handleDownload = () => {
    if (currentImage) {
      const link = document.createElement('a');
      link.href = currentImage.filePath;
      link.download = currentImage.originalFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePrevious, handleNext, onClose]);

  if (!currentImage) {
    return null;
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] p-0">
        <div className="relative flex h-full flex-col">
          {/* Header with controls */}
          <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900" title={currentImage.originalFileName}>
                {currentImage.originalFileName}
              </p>
              <p className="text-xs text-gray-500">
                {currentIndex + 1} of {images.length}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                title="Download"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClose}
                title="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Image display */}
          <div className="relative flex-1 overflow-hidden bg-gray-100">
            <div className="flex h-full items-center justify-center p-4">
              <img
                src={currentImage.filePath}
                alt={currentImage.originalFileName}
                className="max-h-full max-w-full object-contain"
              />
            </div>

            {/* Navigation arrows */}
            {hasPrevious && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handlePrevious}
                className="absolute left-4 top-1/2 h-12 w-12 -translate-y-1/2 rounded-full p-0 shadow-lg"
                title="Previous (←)"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
            )}

            {hasNext && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleNext}
                className="absolute right-4 top-1/2 h-12 w-12 -translate-y-1/2 rounded-full p-0 shadow-lg"
                title="Next (→)"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            )}
          </div>

          {/* Footer with metadata */}
          <div className="border-t border-gray-200 bg-white px-4 py-3">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Uploaded by {currentImage.uploadedBy}</span>
              <span>{new Date(currentImage.uploadedAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
